/**
 * Modal spacing system — ONE source of truth so modals never look squashed and stay
 * consistent across the app. Import these instead of hand-tuning padding/gaps per modal.
 *
 * The scale (8px rhythm, generous on purpose):
 *   - panel padding:        px-6 py-6        (24px) — never less than this inside a modal
 *   - between sections:     space-y-6        (24px) — clear separation, not cramped
 *   - label -> control:     space-y-2 / mb-2 (8px)
 *   - header / footer:      px-6 py-5 / py-4  with a divider
 *   - inputs:               px-3.5 py-2.5     (comfortable hit area, text never clipped)
 *
 * New modals should compose from these; don't reintroduce ad-hoc px-5/py-2/space-y-3.
 */
// Overlay does NOT set a z-index — the consumer adds one (z-50 default, z-[60] for nested) so
// stacking is explicit and predictable.
export const modalOverlay = 'fixed inset-0 flex items-center justify-center bg-black/40 p-4';

/**
 * FLOW surfaces — multi-field / multi-step experiences (pick a team, edit an agent, agent
 * details). The rule: a FLOW is a full-screen PAGE on mobile and a centered card from `sm:` up.
 * Only CONFIRMATION dialogs (yes/no) stay a centered modal on every size — those use
 * `modalOverlay` / `modalPanel`.
 *
 * Usage: `${flowOverlay} z-50` on the overlay; `${flowPanel} sm:max-w-lg sm:h-auto sm:max-h-[90dvh]`
 * on the panel (each consumer sets its own sm width/height; mobile is always full-bleed).
 */
export const flowOverlay = 'fixed inset-0 sm:flex sm:items-center sm:justify-center sm:bg-black/40 sm:p-4';
export const flowPanel = 'flex flex-col bg-white dark:bg-secondary-900 border-secondary-200 dark:border-secondary-700 w-full h-full sm:border sm:rounded-2xl sm:shadow-xl';
export const modalPanel = 'w-full rounded-2xl bg-white dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 shadow-xl flex flex-col';
export const modalHeader = 'flex items-center justify-between px-6 py-5 border-b border-secondary-200 dark:border-secondary-800';
export const modalTitle = 'text-sm font-semibold text-secondary-900 dark:text-white';
export const modalBody = 'overflow-y-auto px-6 py-6 space-y-6';
export const modalFooter = 'flex items-center justify-end gap-3 px-6 py-4 border-t border-secondary-200 dark:border-secondary-800';
/** Section eyebrow label (the small uppercase caption above a control). */
export const modalLabel = 'mb-2 text-[11px] font-medium uppercase tracking-wide text-secondary-400';
/** Standard text input / select / textarea surface. */
export const modalField = 'w-full rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-800 px-3.5 py-2.5 text-sm text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500';
